import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Crown, Calendar, Sparkles, X, TrendingUp, Check } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { openRazorpayCheckout } from "../lib/razorpay";
import { useAuth } from "../context/AuthContext";

function daysUntil(iso) {
  if (!iso) return null;
  try {
    const ms = new Date(iso).getTime() - Date.now();
    return Math.max(0, Math.ceil(ms / 86400000));
  } catch {
    return null;
  }
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-IN", { year: "numeric", month: "long", day: "numeric" });
  } catch {
    return iso;
  }
}

export function SubscriptionPanel() {
  const { user } = useAuth();
  const [sub, setSub] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [showCancel, setShowCancel] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      // Fetch each independently so one failure doesn't blank the whole panel.
      const [meRes, plansRes] = await Promise.allSettled([
        api.get("/subscription/me"),
        api.get("/subscription/plans"),
      ]);
      if (meRes.status === "fulfilled") {
        setSub(meRes.value.data);
      } else {
        console.error("subscription/me failed:", meRes.reason?.response?.status, meRes.reason?.response?.data);
        setSub({ active: false, plan_id: "free" });
        toast.error(formatApiError(meRes.reason));
      }
      if (plansRes.status === "fulfilled") {
        setPlans(plansRes.value.data.plans || []);
      } else {
        console.error("subscription/plans failed:", plansRes.reason?.response?.status);
        setPlans([]);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const startPlusCheckout = async (planId) => {
    setBusy(true);
    try {
      const { data: order } = await api.post("/subscription/create-order", { plan_id: planId });
      await openRazorpayCheckout({
        keyId: order.key_id,
        amount: order.amount,
        currency: order.currency,
        name: "Rate-ED",
        description: order.plan_name,
        orderId: order.order_id,
        prefill: { email: user?.email, name: user?.name },
        onSuccess: async (rzp) => {
          try {
            await api.post("/subscription/verify", rzp);
            toast.success("Explorer Plus activated!");
            await load();
          } catch (err) {
            toast.error(formatApiError(err));
          } finally {
            setBusy(false);
          }
        },
        onDismiss: () => setBusy(false),
      });
    } catch (err) {
      toast.error(formatApiError(err));
      setBusy(false);
    }
  };

  const startUpgrade = async () => {
    setBusy(true);
    try {
      const { data: order } = await api.post("/subscription/upgrade/create-order", { target_plan_id: "plus_yearly" });
      await openRazorpayCheckout({
        keyId: order.key_id,
        amount: order.amount,
        currency: order.currency,
        name: "Rate-ED",
        description: `Upgrade to ${order.plan_name}`,
        orderId: order.order_id,
        prefill: { email: user?.email, name: user?.name },
        onSuccess: async (rzp) => {
          try {
            await api.post("/subscription/upgrade/verify", rzp);
            toast.success("Upgraded to Explorer Plus Yearly!");
            await load();
          } catch (err) {
            toast.error(formatApiError(err));
          } finally {
            setBusy(false);
          }
        },
        onDismiss: () => setBusy(false),
      });
    } catch (err) {
      toast.error(formatApiError(err));
      setBusy(false);
    }
  };

  const cancelAutoRenew = async () => {
    setBusy(true);
    try {
      await api.post("/subscription/cancel");
      toast.success("Auto-renew turned off. Your Plus access stays until expiry.");
      setShowCancel(false);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const resumeAutoRenew = async () => {
    setBusy(true);
    try {
      await api.post("/subscription/resume");
      toast.success("Auto-renew turned back on.");
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-3xl p-8 border border-black/5" data-testid="subscription-panel-loading">
        <p className="text-sm text-[#13A699]/70">Loading subscription…</p>
      </div>
    );
  }

  const isPlus = sub?.active;
  const planId = sub?.plan_id;
  const expiresAt = sub?.expires_at;
  const days = daysUntil(expiresAt);
  const autoRenew = sub?.auto_renew !== false;
  const cancelled = !autoRenew && isPlus;

  const monthly = plans.find((p) => p.id === "plus_monthly");
  const yearly = plans.find((p) => p.id === "plus_yearly");

  return (
    <div className="space-y-6" data-testid="subscription-panel">
      {/* Current plan card */}
      <div
        className="rounded-3xl p-6 sm:p-8 border-2"
        style={{
          borderColor: isPlus ? "#FFD708" : "#13A69940",
          background: isPlus
            ? "linear-gradient(135deg, #FFF7ED 0%, #FFF3C4 100%)"
            : "#FFFFFF",
        }}
        data-testid="subscription-current-card"
      >
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-2">
              {isPlus ? (
                <Crown className="h-6 w-6" style={{ color: "#FFD708" }} fill="#FFD708" />
              ) : (
                <Sparkles className="h-6 w-6" style={{ color: "#13A699" }} />
              )}
              <h3
                className="font-display text-2xl font-bold uppercase"
                style={{ color: "#13A699" }}
                data-testid="subscription-plan-name"
              >
                {isPlus
                  ? planId === "plus_yearly"
                    ? "Explorer Plus · Yearly"
                    : "Explorer Plus · Monthly"
                  : "Explorer Free"}
              </h3>
            </div>
            {isPlus ? (
              <div className="text-sm space-y-1" style={{ color: "#13A699" }}>
                <div className="flex items-center gap-2" data-testid="subscription-expiry">
                  <Calendar className="h-4 w-4" />
                  <span>
                    {cancelled ? "Access ends" : "Renews"} on <b>{formatDate(expiresAt)}</b>
                    {days !== null && <span className="opacity-70"> ({days} day{days === 1 ? "" : "s"} left)</span>}
                  </span>
                </div>
                {cancelled && (
                  <div className="text-xs mt-1 font-semibold text-red-600" data-testid="subscription-cancelled-notice">
                    Auto-renew is OFF &mdash; you won&apos;t be charged again.
                  </div>
                )}
              </div>
            ) : (
              <p className="text-sm" style={{ color: "#13A699" }}>
                You&apos;re on the Free plan. Upgrade for full mentor reviews, contact details, class reminders &amp; more.
              </p>
            )}
          </div>
        </div>

        {/* Actions */}
        {isPlus ? (
          <div className="mt-6 flex items-center gap-3 flex-wrap">
            {planId === "plus_monthly" && !cancelled && (
              <button
                type="button"
                onClick={startUpgrade}
                disabled={busy}
                className="btn-yellow text-sm px-5 py-2.5 inline-flex items-center gap-2 disabled:opacity-60"
                data-testid="subscription-upgrade-yearly-btn"
              >
                <TrendingUp className="h-4 w-4" />
                Upgrade to Yearly · Save ₹589
              </button>
            )}
            {!cancelled && (
              <button
                type="button"
                onClick={() => setShowCancel(true)}
                disabled={busy}
                className="text-sm px-5 py-2.5 rounded-full border border-red-300 text-red-600 hover:bg-red-50 inline-flex items-center gap-2 disabled:opacity-60"
                data-testid="subscription-cancel-btn"
              >
                <X className="h-4 w-4" />
                Cancel Auto-renew
              </button>
            )}
            {cancelled && (
              <button
                type="button"
                onClick={resumeAutoRenew}
                disabled={busy}
                className="btn-yellow text-sm px-5 py-2.5 inline-flex items-center gap-2 disabled:opacity-60"
                data-testid="subscription-resume-btn"
              >
                <Crown className="h-4 w-4" />
                Turn auto-renew back on
              </button>
            )}
          </div>
        ) : (
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            {monthly && (
              <PlanCard plan={monthly} onSubscribe={() => startPlusCheckout("plus_monthly")} busy={busy} highlight={false} testid="plan-card-monthly" />
            )}
            {yearly && (
              <PlanCard plan={yearly} onSubscribe={() => startPlusCheckout("plus_yearly")} busy={busy} highlight={true} testid="plan-card-yearly" />
            )}
          </div>
        )}
      </div>

      {/* Features summary when Plus */}
      {isPlus && (
        <div className="bg-white rounded-3xl p-6 sm:p-8 border border-black/5" data-testid="subscription-features">
          <h4 className="font-display text-lg font-bold uppercase mb-3" style={{ color: "#13A699" }}>
            Your Plus benefits
          </h4>
          <ul className="text-sm space-y-2" style={{ color: "#13A699" }}>
            <li className="flex items-start gap-2"><Check className="h-4 w-4 mt-0.5 shrink-0" /> View all mentor reviews</li>
            <li className="flex items-start gap-2"><Check className="h-4 w-4 mt-0.5 shrink-0" /> Mentor phone & location visible on confirmed bookings</li>
            <li className="flex items-start gap-2"><Check className="h-4 w-4 mt-0.5 shrink-0" /> 24h + 1h email reminders before every class</li>
            <li className="flex items-start gap-2"><Check className="h-4 w-4 mt-0.5 shrink-0" /> Priority customer support</li>
            <li className="flex items-start gap-2"><Check className="h-4 w-4 mt-0.5 shrink-0" /> Early access to new mentors</li>
          </ul>
          <Link
            to="/pricing"
            className="text-xs mt-4 inline-block underline"
            style={{ color: "#13A699" }}
            data-testid="subscription-compare-plans"
          >
            Compare plans →
          </Link>
        </div>
      )}

      {/* Cancel confirmation modal */}
      {showCancel && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="subscription-cancel-modal">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full">
            <h4 className="font-display text-xl font-bold uppercase mb-3" style={{ color: "#13A699" }}>
              Turn off auto-renew?
            </h4>
            <p className="text-sm text-black/70">
              You&apos;ll keep Explorer Plus benefits until <b>{formatDate(expiresAt)}</b>. After that, you&apos;ll switch to the Free plan.
              You can re-subscribe anytime.
            </p>
            <div className="mt-5 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowCancel(false)}
                disabled={busy}
                className="text-sm px-4 py-2 rounded-full border border-black/20"
                data-testid="subscription-cancel-modal-back"
              >
                Keep Plus
              </button>
              <button
                type="button"
                onClick={cancelAutoRenew}
                disabled={busy}
                className="text-sm px-4 py-2 rounded-full bg-red-600 text-white disabled:opacity-60"
                data-testid="subscription-cancel-modal-confirm"
              >
                {busy ? "Cancelling…" : "Yes, cancel auto-renew"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PlanCard({ plan, onSubscribe, busy, highlight, testid }) {
  return (
    <div
      className={`rounded-2xl p-5 border-2 ${highlight ? "border-[#FFD708] bg-[#FFF7ED]" : "border-black/10 bg-white"}`}
      data-testid={testid}
    >
      {highlight && (
        <div className="inline-block text-[10px] font-bold uppercase px-2 py-0.5 rounded bg-[#FFD708] mb-2" style={{ color: "#13A699" }}>
          Best Value · Save ₹589
        </div>
      )}
      <h5 className="font-display text-lg font-bold uppercase" style={{ color: "#13A699" }}>{plan.name}</h5>
      <div className="mt-1 text-2xl font-bold" style={{ color: "#13A699" }}>
        ₹{plan.price_inr}
        <span className="text-sm opacity-70">/{plan.cycle === "yearly" ? "yr" : "mo"}</span>
      </div>
      <ul className="mt-3 text-xs space-y-1" style={{ color: "#13A699" }}>
        {(plan.features || []).slice(0, 5).map((f, i) => (
          <li key={i} className="flex items-start gap-1.5"><Check className="h-3.5 w-3.5 mt-0.5 shrink-0" /> {f}</li>
        ))}
      </ul>
      <button
        type="button"
        onClick={onSubscribe}
        disabled={busy}
        className={`mt-4 w-full text-sm px-4 py-2.5 rounded-full font-bold disabled:opacity-60 ${highlight ? "bg-[#13A699] text-white" : "border border-[#13A699] text-[#13A699]"}`}
        data-testid={`${testid}-subscribe`}
      >
        {busy ? "Please wait…" : `Subscribe · ₹${plan.price_inr}`}
      </button>
    </div>
  );
}
